import h from 'hyperscript';

import helpIcon from '@disy/cadenza-icons/help.svg';
import { assertType } from '@disy/lib/custom-error';
import { icon } from '@disy/lib/icon';
import { logger } from '@disy/lib/logging';
import { createIconButton } from '@disy/ui/button';
import { escapeAndMarkHTML } from '@disy/ui/escape-and-mark';
import { setupPopupButton } from '@disy/ui/popup';

import type { HelpTooltipCallback } from 'cadenza/api-client/help-tooltip-api';
import { getTooltipText } from 'cadenza/api-client/help-tooltip-api';

import './help-tooltip.css';

/*
 * Note: The documentation snippets above each ID are included in the Theming documentation
 * if you enclose it in the help-tooltips tag. So please add the tag only if a tooltip should
 * be published. (All tooltips should be documented, though, even without the enclosing tag.)
 * As the IDs are usually published, please keep them in alphabetical order.
 */
const HELP_TOOLTIP_IDS = [
  /* tag::help-tooltips[]
|advancedLayerSettings.serverSideRendering | In the advanced layer settings for map views; explains the action for server side rendering.
end::help-tooltips[] */
  'advancedLayerSettings.serverSideRendering',
  /* tag::help-tooltips[]
|advancedMapSettings.mapCoupling.limitationHint | In the dialog for map coupling; explains limitation of coupling.
end::help-tooltips[] */
  'advancedMapSettings.mapCoupling.limitationHint',
  /* tag::help-tooltips[]
|advancedMapSettings.mapScaleRange.min and advancedMapSettings.mapScaleRange.max | In the advanced map settings dialog, explains the effect of min and max scale for maps.
  end::help-tooltips[] */
  'advancedMapSettings.mapScaleRange.min',
  'advancedMapSettings.mapScaleRange.max',
  /* tag::help-tooltips[]
  |advancedMapSettings.maxMapExtent | In the advanced map settings, explains the max map extent option.
  end::help-tooltips[] */
  'advancedMapSettings.maxMapExtent',
  /* tag::help-tooltips[]
|advancedMapSettings.overviewMap.requirementsHint | In the advanced map settings, shows requirements for an overview map.
end::help-tooltips[] */
  'advancedMapSettings.overviewMap.requirementsHint',
  /* tag::help-tooltips[]
|arcgis.rasterFunction.argumentsFormat | In the d-arcgis-rendering-rule-editing-dialog; explains the format of the arguments for raster functions.
end::help-tooltips[] */
  'arcgis.rasterFunction.argumentsFormat',
  /* tag::help-tooltips[]
|createRepositoryDialog.dataProtection | In the dialog for creating repositories;  explains the 'data protection/security' option for new repositories.
end::help-tooltips[] */
  'createRepositoryDialog.dataProtection',
  /* tag::help-tooltips[]
|createRepositoryDialog.deletionDeadlines | In the dialog for creating repositories; explains the 'deletion deadlines' option for new repositories.
end::help-tooltips[] */
  'createRepositoryDialog.deletionDeadlines',
  /* tag::help-tooltips[]
|createRepositoryDialog.projects | In the dialog for creating repositories; explains the 'projects' option for new repositories.
end::help-tooltips[] */
  'createRepositoryDialog.projects',
  /* tag::help-tooltips[]
|createRepositoryDialog.publishingControl | In the dialog for creating repositories; explains the 'publishing control' option for new repositories.
end::help-tooltips[] */
  'createRepositoryDialog.publishingControl',
  /* tag::help-tooltips[]
|deletionDeadline.emailNotificationHint | In deletion deadline settings, explains the 'e-mail addresses for notification' option for deletion deadline.
end::help-tooltips[] */
  'deletionDeadline.emailNotificationHint',
  /* tag::help-tooltips[]
|importWizard.objecttype.name | In the wizard for importing data; explains the `Name` property of the imported objecttype.
end::help-tooltips[] */
  'importWizard.objecttype.name',
  /* tag::help-tooltips[]
|importWizard.objecttype.movementProfile.name | In the wizard for importing data; explains the `Name` property of the imported movement profile.
end::help-tooltips[] */
  'importWizard.objecttype.movementProfile.name',
  /* tag::help-tooltips[]
|importWizard.attributes.attributeType | In the wizard for importing data; explains the `Attribute type` property of attribute definitions.
end::help-tooltips[] */
  'importWizard.attributes.attributeType',
  /* tag::help-tooltips[]
|importWizard.attributes.movementProfileAttributes | In the wizard for importing data; explains the `Attribute usage` property of attribute definitions.
end::help-tooltips[] */
  'importWizard.attributes.movementProfileAttributes',
  /* tag::help-tooltips[]
|reportDesigner.mapView.autoZoomHint | In the report template designer, explains the 'auto zoom' behaviour for map vies included in cadenza reports.
end::help-tooltips[] */
  'reportDesigner.mapView.autoZoomHint',
  /* tag::help-tooltips[]
|routing.customSpeed.speedFactor | In the routing sidebar, explains the 'speed factor' number-slider field
end::help-tooltips[] */
  'routing.customSpeed.speedFactor',
  /* tag::help-tooltips[]
|routing.isochrones.smoothing | In the routing sidebar (isochrones mode) and for isochrone (reachability) enrichments, explains the 'level of simplification' number-slider field that controls how result polygons are smoothed by the openrouteservice.
end::help-tooltips[] */
  'routing.isochrones.smoothing',
  /* tag::help-tooltips[]
|scaleRangeVisibility.minValue and scaleRangeVisibility.maxValue | In the layer settings dialog and in the designer for a layer
end::help-tooltips[] */
  'scaleRangeVisibility.minValue',
  'scaleRangeVisibility.maxValue',
  /* tag::help-tooltips[]
  | sketchLayer.circle.radius | In the sketch layer circle item controls, explains how the radius' correctness can only be guaranteed for the distance of the circle's center to its eastern border.
 end::help-tooltips[] */
  'sketchLayer.circle.radius',
  /* tag::help-tooltips[]
|systemSettings.srs.cadenzaManagedDataDefault | In system settings SRS section, explains what the default SRS for Cadenza managed data is.
end::help-tooltips[] */
  'systemSettings.srs.cadenzaManagedDataDefault',
  /* tag::help-tooltips[]
|systemSettings.srs.coordinateDisplayDefault | In system settings SRS section, explains what the default SRS for coordinate display is.
end::help-tooltips[] */
  'systemSettings.srs.coordinateDisplayDefault',
  /* tag::help-tooltips[]
|systemSettings.srs.selectionDialogsDefault | In system settings SRS section, explains what the default SRS for selection dialogs is (at the moment, geodata export dialogs).
end::help-tooltips[] */
  'systemSettings.srs.selectionDialogsDefault',
  /* tag::help-tooltips[]
|worksheetSettings.analysisContext.auto | In the panel for worksheet settings;  explains the 'auto' setting for displaying the analysis context.
end::help-tooltips[] */
  'worksheetSettings.analysisContext.auto',
  /* tag::help-tooltips[]
|worksheetSettings.linearLayout.order | In the panel for worksheet settings;  explains the ordering of the views.
end::help-tooltips[] */
  'worksheetSettings.linearLayout.order',
  /* tag::help-tooltips[]
|view.map.extentFilter | In the designer for map views; explains the action for filtering data views by the map views extent.
end::help-tooltips[] */
  'view.map.extentFilter',
  /* tag::help-tooltips[]
|view.controller.filter | In the designer for controller views; explains the action for filtering data views by selected controller value.
end::help-tooltips[] */
  'view.controller.filter',
] as const;

type HelpTooltipId = (typeof HELP_TOOLTIP_IDS)[number] & `accessSettings.role.${string}`;

let customHelpTooltipCallback: HelpTooltipCallback;

/* For testing and catalog */
export function __setHelpTooltipCallback__(callbackFunction: HelpTooltipCallback) {
  customHelpTooltipCallback = callbackFunction;
}

function getHelpTooltipText(topicId: string): Promise<string> {
  return (customHelpTooltipCallback ?? getTooltipText)(topicId);
}

const COMPONENT_NAME = 'd-help-tooltip';

export class HelpTooltip extends HTMLElement {
  async connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    if (!this.topicId) {
      logger.warn('This help tooltip has no topicId ("data-topic-id" attribute).');
      return;
    }
    if (!this.#initialized) {
      let helpText: string | undefined;
      try {
        helpText = await getHelpTooltipText(this.topicId);
      } catch (error) {
        logger.debug('An error happened getting the help-tooltip content: ', error);
      }

      if (helpText) {
        const popupContent = () => h('.d-stack-v.space-2', { innerHTML: escapeAndMarkHTML(helpText) });
        const helpButton = createIconButton(icon(helpIcon), this.#title ?? '', { variant: 'borderless', size: 'xs' });
        setupPopupButton(helpButton, popupContent, {
          arrow: true,
          placement: 'right-start',
          variant: ['info', 'padding-3'],
        });
        this.append(helpButton);
      }
      this.#setInitialized();
    }
  }

  get #title(): string | null {
    return this.getAttribute('title');
  }

  get topicId() {
    const topicId = this.dataset.topicId;
    if (topicId) {
      assertType<HelpTooltipId>(
        topicId,
        HELP_TOOLTIP_IDS.includes(topicId as HelpTooltipId) || topicId.startsWith('accessSettings.role.'),
      );
      return topicId;
    }
  }

  #setInitialized() {
    this.classList.add('is-initialized');
  }

  get #initialized() {
    return this.classList.contains('is-initialized');
  }
}

customElements.define(COMPONENT_NAME, HelpTooltip);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: HelpTooltip;
  }
}
